import { FC, useState } from 'react';
import { CatButton } from '@blocks-react/components/Button';
import { useTranslations } from 'next-intl';
import { Conditional } from 'components/pcc-components';
import { ProductDealerPriceAvailability } from 'services/dealerPriceAvailability/types';
import Styles from './../parts-overview.module.scss';
import { usePartDiagram } from 'hooks/use-part-diagram/parts-diagram-context';
import { LineItem } from 'types/parts-diagram';
import QuantityInput from 'components/quantity-Input';
import PartsAvailabilityWrapper from 'components/parts-availability-wrapper';
import { useCoreContext } from 'providers/core-provider';
import { useQuickToast } from 'hooks/use-toast';
import { getPaymentMethods } from 'services/parts-diagram/actions';
import { GA_PDP_DIAGRAM } from 'constants/analyticsConstants';
import {
  getFormattedItemData,
  getFormattedItemSummary
} from 'components/product-comparison-grid/product-comparison-card/utils';
import { useSelectItemEvent } from 'hooks/analytics/use-select-item-event';
import { useAddToCartItemEvent } from 'hooks/analytics/use-add-to-cart-item';

export interface PriceAndAvailabilityProps {
  lineItem: LineItem;
  handleQuantityChange: (value: string) => void;
  modifiedPriceData: ProductDealerPriceAvailability;
  disableRow: boolean;
  isLoading: boolean;
}

export const PriceAndAvailability: FC<PriceAndAvailabilityProps> = ({
  lineItem,
  handleQuantityChange,
  modifiedPriceData,
  disableRow,
  isLoading
}) => {
  const t = useTranslations();
  const toast = useQuickToast();
  const {
    isCatCorp,
    disableAddToCart,
    analyticsCommonInfo: { locale } = {},
    serverData,
    setDisableAddToCart
  } = usePartDiagram();
  const { fireEvent: fireSelectItemEvent } = useSelectItemEvent();
  const { fireEvent: addToCartItemEvent } = useAddToCartItemEvent();
  const { handleAddToCart } = useCoreContext();
  const {
    quantity,
    disambiguation,
    ieSystemControlNumber,
    priceAndAvailabilityData,
    partNumber
  } = lineItem;

  const [modifiedQuantity, setModifiedQuantity] = useState(
    quantity.trim().length === 0 ? '1' : quantity
  );

  const handleAddToCartClick = async () => {
    const partItem = {
      partNumber: lineItem.partNumber,
      partName: lineItem.name,
      quantity: modifiedQuantity,
      sosCode: lineItem?.priceAndAvailabilityData?.sosCode
    };
    setDisableAddToCart(true);
    const { error, response } = await handleAddToCart(
      { items: [partItem] },
      serverData.dealerInformation.storeId,
      locale
    );
    if (error) {
      toast.error({
        message: t('ITEM_ADD_ERROR')
      });
    } else {
      toast.success({
        message: t('ITEM_ADDED_TO_CART')
      });

      const { orderId } = response;
      const { paymentMethods } = await getPaymentMethods(orderId);

      if (paymentMethods) {
        const priceAndAvailabilityFormatedData = {
          ...lineItem.priceAndAvailabilityData,
          type: '',
          name: lineItem.name,
          imageURL: '',
          index: parseInt(lineItem?.referenceNumber)
        };
        fireSelectItemEvent({
          item: {
            orderItemId: orderId,
            partNumber: partItem.partNumber,
            quantity: priceAndAvailabilityFormatedData.quantity,
            availabilityMessage: priceAndAvailabilityFormatedData
              ? priceAndAvailabilityFormatedData.availability?.[0]
                  ?.availabilityMessage
              : '',
            totalPrice: priceAndAvailabilityFormatedData
              ? priceAndAvailabilityFormatedData.unformattedTotalPrice &&
                Number(priceAndAvailabilityFormatedData.unformattedTotalPrice)
              : 0
          },
          index: parseInt(lineItem?.referenceNumber),
          partName: lineItem && `${lineItem?.partNumber}: ${lineItem?.name}`,
          locationId: '',
          itemSummary: {
            categoryPath: priceAndAvailabilityFormatedData.categoryPath,
            manufacturer: ''
          },
          isSearchPageEvent: true,
          itemListId: GA_PDP_DIAGRAM,
          itemListName: GA_PDP_DIAGRAM
        });

        addToCartItemEvent({
          locationId: '',
          index: parseInt(lineItem?.referenceNumber),
          itemSummary: getFormattedItemSummary(
            priceAndAvailabilityFormatedData
          ),
          itemData: getFormattedItemData(
            orderId,
            priceAndAvailabilityFormatedData
          ),
          itemListId: GA_PDP_DIAGRAM,
          itemListName: GA_PDP_DIAGRAM
        });
      }
    }
    setDisableAddToCart(false);
  };

  const isReplacedPart =
    priceAndAvailabilityData?.indirectReplacement ||
    (Array.isArray(priceAndAvailabilityData?.replacementParts) &&
      priceAndAvailabilityData?.replacementParts.length > 0);

  return (
    <>
      <p className="cat-u-theme-typography-label-sm m-0">
        {`${t('QUANTITY1')}:`}
      </p>

      <div className={Styles['parts-overview-stepper']}>
        <QuantityInput
          inputValue={modifiedQuantity}
          disabled={disableRow}
          handleQuantityChange={(value: string) => {
            setModifiedQuantity(value);
            handleQuantityChange(value);
          }}
        />
      </div>

      {!disambiguation && (
        <Conditional
          test={!!ieSystemControlNumber}
          fallback={
            !isCatCorp &&
            !isReplacedPart &&
            priceAndAvailabilityData?.enableAddToCart && (
              <CatButton
                size="sm"
                variant="primary"
                disabled={
                  disableAddToCart ||
                  disableRow ||
                  modifiedQuantity === '0' ||
                  modifiedQuantity === undefined
                }
                onClick={handleAddToCartClick}
              >
                {t('ADD_TO_CART')}
              </CatButton>
            )
          }
        />
      )}
      <div>
        <PartsAvailabilityWrapper
          availabilityData={
            partNumber === modifiedPriceData?.partNumber
              ? modifiedPriceData
              : priceAndAvailabilityData
          }
          shouldDisplayLabel={false}
          isLoading={isLoading}
        />
      </div>
    </>
  );
};
